import { enContent } from "../content/locales/en";
import { svContent } from "../content/locales/sv";
import { findDescriptiveArticleByPath, type DescriptiveArticleContent } from "../content/articles";
import {
  getFortnoxPublicPageByPath,
  type FortnoxPublicPageContent,
  type FortnoxPublicPageKey
} from "../content/fortnoxPublicPages";
import type { LandingPageContent, Locale } from "../content/types";

type BaseAppRoute = {
  locale: Locale;
  alternatePath: string;
  content: LandingPageContent;
};

export type LandingAppRoute = BaseAppRoute & {
  kind: "landing";
  path: "/" | "/en";
};

export type ArticleAppRoute = BaseAppRoute & {
  kind: "article";
  locale: "sv";
  path: DescriptiveArticleContent["path"];
  article: DescriptiveArticleContent;
  canonicalUrl: DescriptiveArticleContent["canonicalUrl"];
  shouldRedirectToCanonical: boolean;
};

export type CrmAppRoute = BaseAppRoute & {
  kind: "crm";
  path: "/crm";
  appPath: string;
};

export type ProposalOnboardingAppRoute = BaseAppRoute & {
  kind: "proposal_onboarding";
  path: "/proposal-onboarding";
};

export type AccountAppRoute = BaseAppRoute & {
  kind: "account";
  path:
    | "/account"
    | "/en/account"
    | "/account/integrationer/fortnox"
    | "/en/account/integrations/fortnox"
    | "/account/integrations/quickbooks";
  accountPanel: "fortnox" | "quickbooks";
};

export type QuickBooksPublicAppRoute = BaseAppRoute & {
  kind: "quickbooks_public";
  locale: "en";
  path: "/quickbooks";
};

export type QuickBooksActionAppRoute = BaseAppRoute & {
  kind: "quickbooks_action";
  locale: "en";
  path: "/quickbooks/launch" | "/quickbooks/connect" | "/quickbooks/reconnect" | "/quickbooks/disconnect";
  action: "launch" | "connect" | "reconnect" | "disconnect";
};

export type QuickBooksLegalAppRoute = BaseAppRoute & {
  kind: "quickbooks_legal";
  locale: "en";
  path: "/quickbooks/terms" | "/quickbooks/privacy";
  documentKind: "terms" | "privacy";
};

export type FortnoxPublicAppRoute = BaseAppRoute & {
  kind: "fortnox_public";
  locale: "sv" | "en";
  path: FortnoxPublicPageContent["path"];
  pageKey: FortnoxPublicPageKey;
  page: FortnoxPublicPageContent;
};

export type NotFoundAppRoute = BaseAppRoute & {
  kind: "not_found";
  path: string;
  title: string;
  message: string;
};

export type MarketIntakeAppRoute = BaseAppRoute & { kind: "market_intake" | "us_market_onboarding" | "us_landing" | "us_services"; path: string };

export type AppRoute =
  | MarketIntakeAppRoute
  | LandingAppRoute
  | ArticleAppRoute
  | CrmAppRoute
  | ProposalOnboardingAppRoute
  | AccountAppRoute
  | QuickBooksPublicAppRoute
  | QuickBooksActionAppRoute
  | QuickBooksLegalAppRoute
  | FortnoxPublicAppRoute
  | NotFoundAppRoute;

const productionSvOrigin = "https://dynamicbooks.se";
const productionEnOrigin = "https://dynamicbooks.app";

const articlePageShellContent = {
  ...svContent,
  navigation: {
    ...svContent.navigation,
    items: svContent.navigation.items.map((item) => ({
      ...item,
      href: rootPrefixHashHref(item.href)
    }))
  },
  footer: {
    ...svContent.footer,
    groups: svContent.footer.groups.map((group) => ({
      ...group,
      links: group.links.map((link) => ({
        ...link,
        href: rootPrefixHashHref(link.href)
      }))
    }))
  }
} satisfies LandingPageContent;

function rootPrefixHashHref(href: string): string {
  return href.startsWith("#") ? `/${href}` : href;
}

function normalizeHash(hash: string): "" | `#${string}` {
  return hash.startsWith("#") ? (hash as `#${string}`) : "";
}

export function resolveAppRoute(pathname = "/", hash = "", hostname?: string | null, search = ""): AppRoute {
  if (/^\/us\/services\/*$/.test(pathname)) {
    return { kind: "us_services", path: "/us/services", locale: "en", alternatePath: "https://dynamicbooks.se/", content: enContent };
  }
  const normalizedPath = (pathname === "" ? "/" : pathname).replace(/^(\/(?:us\/intake|us\/proposal-onboarding\/[^/]+|en))\/+$/,"$1");
  if (normalizedPath === "/us/intake" || normalizedPath.startsWith("/us/proposal-onboarding/")) {
    return { kind: normalizedPath === "/us/intake" ? "market_intake" : "us_market_onboarding", path: normalizedPath, locale: "en", alternatePath: "https://dynamicbooks.se/", content: enContent };
  }
  const normalizedHash = normalizeHash(hash);
  const host = normalizeHostname(hostname);
  const splitProductionHost = isSplitProductionHost(host);
  if ((host === "dynamicbooks.app" || host === "www.dynamicbooks.app") && (normalizedPath === "/" || normalizedPath === "/en" || normalizedPath === "/en/")) {
    return { kind: "us_landing", path: normalizedPath, locale: "en", alternatePath: "https://dynamicbooks.se/" + normalizedHash, content: enContent };
  }
  const hostDefaultLocale: Locale = host === "dynamicbooks.app" || host === "www.dynamicbooks.app" ? "en" : "sv";
  const article = findDescriptiveArticleByPath(normalizedPath);
  const localizedContent = hostDefaultLocale === "en" ? enContent : svContent;

  if (isCrmAppPath(normalizedPath)) {
    return {
      kind: "crm",
      locale: hostDefaultLocale,
      path: "/crm",
      appPath: normalizedPath,
      alternatePath: "/crm",
      content: localizedContent
    };
  }

  if (isProposalOnboardingAppPath(normalizedPath)) {
    return {
      kind: "proposal_onboarding",
      locale: hostDefaultLocale,
      path: "/proposal-onboarding",
      alternatePath: hostDefaultLocale === "en" ? "/" : "/en",
      content: localizedContent
    };
  }

  if (normalizedPath === "/quickbooks") {
    return { kind: "quickbooks_public", locale: "en", path: "/quickbooks", alternatePath: productionSvOrigin, content: enContent };
  }

  const quickBooksLegalKind = normalizedPath.match(/^\/quickbooks\/(terms|privacy)\/?$/)?.[1] as QuickBooksLegalAppRoute["documentKind"] | undefined;
  if (quickBooksLegalKind) {
    return {
      kind: "quickbooks_legal",
      locale: "en",
      path: `/quickbooks/${quickBooksLegalKind}`,
      documentKind: quickBooksLegalKind,
      alternatePath: productionSvOrigin,
      content: enContent
    };
  }

  const quickBooksAction = normalizedPath.match(/^\/quickbooks\/(launch|connect|reconnect|disconnect)\/?$/)?.[1] as QuickBooksActionAppRoute["action"] | undefined;
  if (quickBooksAction) {
    return {
      kind: "quickbooks_action",
      locale: "en",
      path: `/quickbooks/${quickBooksAction}`,
      action: quickBooksAction,
      alternatePath: productionSvOrigin,
      content: enContent
    };
  }

  const accountRoute = resolveAccountAppPath(normalizedPath);
  if (accountRoute) {
    return {
      kind: "account",
      locale: accountRoute.locale,
      path: accountRoute.path,
      accountPanel: accountRoute.panel,
      alternatePath: buildAccountAlternatePath(accountRoute.locale, splitProductionHost),
      content: accountRoute.locale === "en" ? enContent : svContent
    };
  }

  const fortnoxLocale = new URLSearchParams(search).get("lang") === "en" ? "en" : "sv";
  const fortnoxPublicPage = getFortnoxPublicPageByPath(normalizedPath, fortnoxLocale);
  if (fortnoxPublicPage) {
    return {
      kind: "fortnox_public",
      locale: fortnoxPublicPage.locale,
      path: fortnoxPublicPage.path,
      pageKey: fortnoxPublicPage.key,
      page: fortnoxPublicPage,
      alternatePath: fortnoxLocale === "sv" ? `${fortnoxPublicPage.path}?lang=en` : fortnoxPublicPage.path,
      content: fortnoxLocale === "sv" ? fortnoxPublicPageShellContent : enContent
    };
  }

  if (isFortnoxPublicPath(normalizedPath)) {
    return buildNotFoundRoute(
      normalizedPath,
      fortnoxLocale === "sv" ? "Fortnox-sidan finns inte" : "Fortnox page not found",
      fortnoxLocale === "sv"
        ? "Sidan kunde inte hittas. Gå till Fortnox-översikten för aktuell information."
        : "The page could not be found. Open the Fortnox overview for current information.",
      fortnoxLocale
    );
  }

  if (article) {
    return {
      kind: "article",
      locale: article.locale,
      path: article.path,
      alternatePath: buildArticleLocaleSwitchPath(splitProductionHost),
      content: articlePageShellContent,
      article,
      canonicalUrl: article.canonicalUrl,
      shouldRedirectToCanonical: isProductionAppHost(host)
    };
  }

  const isEnglish = normalizedPath === "/en" || normalizedPath.startsWith("/en/") || (hostDefaultLocale === "en" && !isSwedishPath(normalizedPath));
  const casePathMatch = normalizedPath.match(/^\/(?:en\/)?case(?:\/([^/]+))?$/);

  if (isEnglish) {
    return {
      kind: "landing",
      locale: "en",
      path: splitProductionHost ? "/" : "/en",
      alternatePath: buildAlternatePath("sv", casePathMatch?.[1], normalizedHash, splitProductionHost),
      content: enContent
    };
  }

  return {
    kind: "landing",
    locale: "sv",
    path: "/",
    alternatePath: buildAlternatePath("en", casePathMatch?.[1], normalizedHash, splitProductionHost),
    content: svContent
  };
}

export function getBrowserRoute(): AppRoute {
  if (typeof window === "undefined") {
    return resolveAppRoute("/");
  }

  return resolveAppRoute(window.location.pathname, window.location.hash, window.location.hostname, window.location.search);
}

function buildAlternatePath(
  locale: Locale,
  encodedCaseId: string | undefined,
  normalizedHash: "" | `#${string}`,
  splitProductionHost: boolean
): string {
  if (splitProductionHost) {
    const origin = locale === "en" ? productionEnOrigin : productionSvOrigin;
    const path = encodedCaseId ? `/case/${encodedCaseId}` : "/";
    return `${origin}${path}${encodedCaseId ? "" : normalizedHash}`;
  }

  if (encodedCaseId) {
    return buildCaseAlternatePath(locale, encodedCaseId);
  }

  return locale === "en"
    ? `/en${normalizedHash}`
    : normalizedHash ? `/${normalizedHash}` : "/";
}

function buildArticleLocaleSwitchPath(splitProductionHost: boolean): string {
  return splitProductionHost ? productionEnOrigin : "/en";
}

const fortnoxPublicPageShellContent = {
  ...svContent,
  navigation: {
    ...svContent.navigation,
    items: svContent.navigation.items.map((item) => ({
      ...item,
      href: rootPrefixHashHref(item.href)
    }))
  },
  footer: {
    ...svContent.footer,
    groups: svContent.footer.groups.map((group) => ({
      ...group,
      links: group.links.map((link) => ({
        ...link,
        href: rootPrefixHashHref(link.href)
      }))
    }))
  }
} satisfies LandingPageContent;

function buildAccountAlternatePath(locale: Locale, splitProductionHost: boolean): string {
  if (splitProductionHost) {
    return locale === "en" ? `${productionSvOrigin}/account` : `${productionEnOrigin}/account`;
  }

  return locale === "en" ? "/account" : "/en/account";
}

function buildCaseAlternatePath(locale: Locale, encodedCaseId?: string): string {
  const basePath = locale === "en" ? "/en/case" : "/case";
  return encodedCaseId ? `${basePath}/${encodedCaseId}` : basePath;
}

function isSplitProductionHost(hostname: string | null): boolean {
  return hostname === "dynamicbooks.se"
    || hostname === "www.dynamicbooks.se"
    || hostname === "dynamicbooks.app"
    || hostname === "www.dynamicbooks.app";
}

function isSwedishPath(pathname: string): boolean {
  return pathname === "/sv" || pathname.startsWith("/sv/");
}

function isCrmAppPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/crm") {
    return true;
  }
  if (!normalized.startsWith("/crm/")) {
    return false;
  }
  return !isCrmPublicAuthPath(normalized);
}

function isCrmPublicAuthPath(pathname: string): boolean {
  return pathname === "/crm/login"
    || pathname === "/crm/logout"
    || pathname === "/crm/auth/google/start"
    || pathname === "/crm/auth/google/callback";
}

function isProposalOnboardingAppPath(pathname: string): boolean {
  return /^\/proposal-onboarding(?:\/[^/]+)?\/?$/.test(pathname);
}

function isFortnoxPublicPath(pathname: string): boolean {
  return pathname === "/fortnox" || pathname.startsWith("/fortnox/");
}

function resolveAccountAppPath(pathname: string): { path: AccountAppRoute["path"]; locale: Locale; panel: AccountAppRoute["accountPanel"] } | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/account") {
    return { path: "/account", locale: "sv", panel: "fortnox" };
  }
  if (normalized === "/en/account") {
    return { path: "/en/account", locale: "en", panel: "fortnox" };
  }
  if (normalized === "/account/integrationer/fortnox") {
    return { path: "/account/integrationer/fortnox", locale: "sv", panel: "fortnox" };
  }
  if (normalized === "/en/account/integrations/fortnox") {
    return { path: "/en/account/integrations/fortnox", locale: "en", panel: "fortnox" };
  }
  if (normalized === "/account/integrations/quickbooks") {
    return { path: "/account/integrations/quickbooks", locale: "en", panel: "quickbooks" };
  }
  return null;
}

function isProductionAppHost(hostname: string | null): boolean {
  return hostname === "dynamicbooks.app" || hostname === "www.dynamicbooks.app";
}

function normalizeHostname(hostname: string | null | undefined): string | null {
  const normalized = hostname?.trim().toLowerCase();
  return normalized ? normalized.replace(/:\d+$/, "") : null;
}

function buildNotFoundRoute(
  path: string,
  title: string,
  message: string,
  locale: Locale = "sv"
): NotFoundAppRoute {
  const content = locale === "en" ? enContent : svContent;
  return {
    kind: "not_found",
    locale,
    path,
    alternatePath: locale === "en" ? "/" : "/en",
    content,
    title,
    message
  };
}
